import {
  DEFAULT_MAX_RETURNS_LAST_30_DAYS,
  nextTriState,
  type PortalFilters,
  type TriState,
} from "./customer-portal-types";

const PORTAL_CHOICES = [
  { key: "all", label: "Tous" },
  { key: "on", label: "Nouveau portail" },
  { key: "off", label: "Ancien portail" },
] as const satisfies readonly { key: TriState; label: string }[];

const FEATURE_FILTERS = [
  { key: "exchanges", label: "Échanges" },
  { key: "exchangeCredit", label: "Échanges crédit" },
  { key: "giftCode", label: "Avoirs" },
] as const satisfies readonly { key: keyof PortalFilters; label: string }[];

const CUSTOMISATION_FILTERS = [
  { key: "customColors", label: "Couleurs perso" },
  { key: "customCss", label: "CSS perso" },
] as const satisfies readonly { key: keyof PortalFilters; label: string }[];

const BUTTON_CLASS =
  "rounded-md border bg-background px-3 py-1.5 text-sm font-medium transition-colors hover:bg-muted/60";

function TriStateButton({
  label,
  state,
  onClick,
}: {
  label: string;
  state: TriState;
  onClick: () => void;
}) {
  return (
    <button
      onClick={onClick}
      className={[
        BUTTON_CLASS,
        state === "on"
          ? "border-emerald-400 text-emerald-600 ring-1 ring-emerald-200"
          : state === "off"
            ? "border-gray-400 text-gray-600 ring-1 ring-gray-200"
            : "",
      ].join(" ")}
    >
      {label}
      {state !== "all" && (
        <span className="ml-1 text-xs">{state === "on" ? "✓" : "✕"}</span>
      )}
    </button>
  );
}

function MaxReturnsFilter({
  max,
  onChange,
}: {
  max: number | null;
  onChange: (max: number | null) => void;
}) {
  return (
    <div className="flex items-center gap-1">
      <button
        onClick={() =>
          onChange(max === null ? DEFAULT_MAX_RETURNS_LAST_30_DAYS : null)
        }
        className={[
          BUTTON_CLASS,
          max !== null
            ? "border-emerald-400 text-emerald-600 ring-1 ring-emerald-200"
            : "",
        ].join(" ")}
      >
        Exclure les gros shops
      </button>

      {max !== null && (
        <label className="flex items-center gap-1 rounded-md border bg-background px-3 py-1.5 text-sm text-muted-foreground">
          <span>&gt;</span>
          <input
            type="number"
            min={0}
            value={max}
            onChange={(e) =>
              onChange(e.target.value === "" ? 0 : Number(e.target.value))
            }
            className="w-20 bg-transparent text-sm font-medium text-foreground focus:outline-none"
          />
          <span>retours / 30j</span>
        </label>
      )}
    </div>
  );
}

function Separator() {
  return <div className="w-px h-5 bg-border" />;
}

export function CustomerPortalFilters({
  filters,
  onChange,
  newCount,
  totalCount,
}: {
  filters: PortalFilters;
  onChange: (filters: PortalFilters) => void;
  newCount: number;
  totalCount: number;
}) {
  const toggle = (key: keyof PortalFilters) => () =>
    onChange({ ...filters, [key]: nextTriState(filters[key] as TriState) });

  return (
    <div className="flex flex-wrap items-center gap-2">
      <div className="flex items-center rounded-md border bg-background px-3 py-1.5 text-sm font-medium">
        <span className="font-semibold text-violet-600">{newCount}</span>
        <span className="ml-1 text-muted-foreground">
          / {totalCount} shops sur le nouveau portail
        </span>
      </div>

      <Separator />

      <input
        type="text"
        value={filters.search}
        onChange={(e) => onChange({ ...filters, search: e.target.value })}
        placeholder="Rechercher un shop…"
        className="w-64 rounded-md border bg-background px-3 py-1.5 text-sm placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-violet-500"
      />

      {PORTAL_CHOICES.map(({ key, label }) => (
        <button
          key={key}
          onClick={() => onChange({ ...filters, portal: key })}
          className={[
            BUTTON_CLASS,
            filters.portal === key
              ? "border-violet-400 text-violet-600 ring-1 ring-violet-200"
              : "",
          ].join(" ")}
        >
          {label}
        </button>
      ))}

      <Separator />

      {FEATURE_FILTERS.map(({ key, label }) => (
        <TriStateButton
          key={key}
          label={label}
          state={filters[key] as TriState}
          onClick={toggle(key)}
        />
      ))}

      <Separator />

      {CUSTOMISATION_FILTERS.map(({ key, label }) => (
        <TriStateButton
          key={key}
          label={label}
          state={filters[key]}
          onClick={toggle(key)}
        />
      ))}

      <Separator />

      <TriStateButton
        label="Actif"
        state={filters.active}
        onClick={toggle("active")}
      />

      <TriStateButton
        label="Retours < 30j"
        state={filters.hasRecentReturns}
        onClick={toggle("hasRecentReturns")}
      />

      <MaxReturnsFilter
        max={filters.maxReturnsLast30Days}
        onChange={(maxReturnsLast30Days) =>
          onChange({ ...filters, maxReturnsLast30Days })
        }
      />
    </div>
  );
}
